import { Badge, Divider, Select, Text, TextField, Button, ButtonGroup, BlockStack, InlineStack, Box } from "@shopify/polaris";
import { useEffect, useState } from "react";
import { formatFloatToString, getRequest, patchRequest, postRequest } from "../../shared/components/functions";

const STOCK_CHANGE_MODES = {
  DIFFERENCE: "difference",
  TOTAL: "total",
};

const initialStock = { Aanpassing: "", CurrentStock: "", StockAanpassingTypeID: "1", Source: "" };

const sanitizeStockInput = (value) => {
  const stockValue = value.replace(/[^0-9.,]/g, "").replace(/,/g, ".");
  const dotCount = stockValue.split(".").length - 1;

  return dotCount > 1 ? stockValue.slice(0, -1) : stockValue;
};

const getStockQuantity = (stockData) => {
  const quantity = parseFloat(stockData?.aantal);
  return Number.isFinite(quantity) ? quantity : 0;
};

const handleInventory = async (isPatch, productId, aantalValue) => {
  if (isPatch) {
    await patchRequest(`/api/inventory/${productId}`, { stockdiff: aantalValue });
  } else {
    await postRequest("/api/inventory", {
      productId: productId,
      stockplaats: 1,
      rack: 0,
      legplank: 0,
      aantal: aantalValue,
    });
  }
};

export const CurrentStock = ({ stockData, fetchInventory, fetchStockHistory, productID }) => {
  const [stockOptions, setStockOptions] = useState([]);

  const [newStock, setNewStock] = useState(initialStock);
  const [adjustStockLoading, setAdjustStockLoading] = useState(false);
  const [sign, setSign] = useState("+");
  const [stockChangeMode, setStockChangeMode] = useState(STOCK_CHANGE_MODES.DIFFERENCE);

  const handleStockChange = (value, id) => {
    let stockvalue = value;
    if (id === "Aanpassing" || id === "CurrentStock") stockvalue = sanitizeStockInput(stockvalue);

    setNewStock((newStock) => ({ ...newStock, [id]: stockvalue }));
  };

  useEffect(() => {
    async function fetchData() {
      const data = await getRequest("/api/stockchanges/adjustment/types");
      const options = data.map((o) => ({ value: String(o.StockAanpassingTypeID), label: o.Type }));
      setStockOptions(options);
    }
    fetchData();
  }, []);

  const currentStock = getStockQuantity(stockData);
  const stockInput = stockChangeMode === STOCK_CHANGE_MODES.TOTAL ? newStock.CurrentStock : newStock.Aanpassing;
  const stockInputValue = parseFloat(stockInput);
  const stockDiff =
    stockChangeMode === STOCK_CHANGE_MODES.TOTAL ? stockInputValue - currentStock : sign === "-" ? -Math.abs(stockInputValue) : stockInputValue;
  const roundedDiff = Math.round(stockDiff * 100) / 100;
  const newValueButton = stockChangeMode === STOCK_CHANGE_MODES.TOTAL ? stockInputValue : Math.round((currentStock + roundedDiff) * 100) / 100;
  const reasonIsMissing = !newStock.Source.trim();
  const typeIsMissing = !newStock.StockAanpassingTypeID;
  const stockInputIsMissing = stockInput === "" || !Number.isFinite(stockInputValue);
  const stockDiffIsZero = !stockInputIsMissing && roundedDiff === 0;
  const adjustButtonDisabled = adjustStockLoading || stockInputIsMissing || stockDiffIsZero || reasonIsMissing || typeIsMissing;

  const handleStockAdjust = async () => {
    if (stockInputIsMissing) return alert("Stockchange needs a value");
    if (stockDiffIsZero) return alert(`Stockchange can't be 0`);
    if (reasonIsMissing) return alert("Reason for change is required");
    if (typeIsMissing) return alert("Stock change type is required");

    setAdjustStockLoading(true);

    const payload = {
      part_id: productID,
      stock_diff: roundedDiff,
      timestamp: new Date(),
      source: newStock.Source.trim(),
      stockchange_type_id: parseInt(newStock.StockAanpassingTypeID),
    };

    try {
      const stockChange = await postRequest("/api/stockchanges", payload);
      if (!stockChange) return alert("Stockchange could not be logged. Inventory was not updated.");

      await handleInventory(!Array.isArray(stockData), productID, roundedDiff);
      await fetchStockHistory();
      await fetchInventory();
      setNewStock(initialStock);
      setSign("+");
    } finally {
      setAdjustStockLoading(false);
    }
  };

  // const fastAdjustPosValues = [1, 2, 3, 4, 5, 10, 20, 50, 100];
  // const fastAdjustNegValues = [-1, -2, -3, -4, -5, -10, -20, -50, -100];

  return (
    <div>
      <BlockStack gap="300">
        <CurrentStockTable stockData={stockData} />
        {/* <Text variant="headingLg" as="h4">
        Fast adjust:
      </Text>
      <Stack vertical>
        <Stack distribution="equalSpacing">
          {fastAdjustPosValues.map((v, idx) => (
            <FastAdjustButton
              key={idx}
              value={v}
              stockData={stockData}
              productID={productID}
              fetchInventory={fetchInventory}
              fetchStockHistory={fetchStockHistory}
            />
          ))}
        </Stack>
        <Stack distribution="equalSpacing">
          {fastAdjustNegValues.map((v, idx) => (
            <FastAdjustButton
              key={idx}
              value={v}
              stockData={stockData}
              productID={productID}
              fetchInventory={fetchInventory}
              fetchStockHistory={fetchStockHistory}
            />
          ))}
        </Stack> 
      </Stack> */}

        <BlockStack gap="300">
          <Text variant="headingMd" as="h5">
            Stock change:
          </Text>

          <ButtonGroup variant="segmented">
            <Button pressed={stockChangeMode === STOCK_CHANGE_MODES.DIFFERENCE} onClick={() => setStockChangeMode(STOCK_CHANGE_MODES.DIFFERENCE)}>
              Difference
            </Button>
            <Button pressed={stockChangeMode === STOCK_CHANGE_MODES.TOTAL} onClick={() => setStockChangeMode(STOCK_CHANGE_MODES.TOTAL)}>
              Current stock
            </Button>
          </ButtonGroup>

          <InlineStack gap="200" blockAlign="center" wrap={false}>
            {stockChangeMode === STOCK_CHANGE_MODES.DIFFERENCE ? (
              <InlineStack gap="200" wrap={false}>
                <ButtonGroup variant="segmented">
                  <Button size="large" pressed={sign === "+"} onClick={() => setSign("+")}>
                    +
                  </Button>
                  <Button size="large" pressed={sign === "-"} onClick={() => setSign("-")}>
                    -
                  </Button>
                </ButtonGroup>
                <TextField label="Difference" labelHidden id="Aanpassing" value={newStock.Aanpassing} onChange={handleStockChange} prefix={sign} />
              </InlineStack>
            ) : (
              <Box width="100%">
                <TextField label="Current stock" id="CurrentStock" value={newStock.CurrentStock} onChange={handleStockChange} />
              </Box>
            )}
            <Box width="100%">
              <Select label="Change type" id="StockAanpassingTypeID" value={newStock.StockAanpassingTypeID} options={stockOptions} onChange={handleStockChange} />
            </Box>
          </InlineStack>

          <TextField label="Reason" id="Source" value={newStock.Source} placeholder="Reason for change" onChange={handleStockChange} />
          <Box>
            <Button size="large" loading={adjustStockLoading} disabled={adjustButtonDisabled} onClick={handleStockAdjust}>
              {!stockInputIsMissing && !stockDiffIsZero
                ? stockChangeMode === STOCK_CHANGE_MODES.TOTAL
                  ? `SET STOCK TO ${formatFloatToString(newValueButton)}`
                  : `ADJUST STOCK TO TOTAL OF ${formatFloatToString(newValueButton)}`
                : "Adjust stock"}
            </Button>
          </Box>
        </BlockStack>
      </BlockStack>
    </div>
  );
};

const CurrentStockTable = ({ stockData }) => {
  const currentStock = getStockQuantity(stockData);

  return (
    <BlockStack gap="200">
      <InlineStack align="space-between">
        <Text fontWeight="bold" variant="headingMd" as="h5">
          Stock
        </Text>
        <Text fontWeight="bold" alignment="end" variant="headingMd" as="h5">
          Quantity
        </Text>
      </InlineStack>
      <Divider />
      <InlineStack align="space-between">
        <Text variant="headingMd" as="h5">
          Total available:
        </Text>
        <Box>
          <Badge>
            <Text fontWeight="bold" variant="headingMd" as="h6">
              {currentStock.toFixed(2).replace(".", ",")}
            </Text>
          </Badge>
        </Box>
      </InlineStack>
    </BlockStack>
  );
};

// const FastAdjustButton = ({
//   value,
//   stockData,
//   fetchInventory,
//   fetchStockHistory,
//   productID,
// }) => {
//   const [loading, setLoading] = useState(false);
//   async function handleClick() {
//     setLoading(true);
//     const payload = {
//       part_id: productID,
//       stock_diff: value,
//       timestamp: new Date(),
//       source: "Fast adjust",
//       stockchange_type_id: 1,
//     };
//     await postRequest("/api/stockchanges", payload);
//     await handleInventory(!Array.isArray(stockData), productID, value);
//     await fetchInventory();
//     await fetchStockHistory();
//     setLoading(false);
//   }
//   return (
//     <Button
//       loading={loading}
//       onClick={handleClick}
//       size="large"
//       primary={value > 0}
//       destructive={value < 0}
//     >
//       <div style={{ width: "40px" }}>{value > 0 ? `+${value}` : value}</div>
//     </Button>
//   );
// };
